import { ArrowLeft } from "lucide-react";

import { formatUyMinor } from "@/lib/formatUyMinor";
import { useTranslation } from "react-i18next";

/**
 * The WHITE wordmark, used directly rather than through BrandLogo.
 *
 * BrandLogo picks its variant from the app's dark-mode class, and this panel is
 * dark in BOTH themes — so on a light-themed page it handed back the navy
 * wordmark, invisible on navy. Filtering it (brightness-0 invert) made it
 * visible and destroyed it: the mark flattened into a white blob, because a
 * filter cannot know which parts of a logo are the shape and which are the
 * ground.
 *
 * The white asset exists for exactly this. Same reason --color-auth-surface
 * exists beside --color-primary: a surface that is always dark needs assets
 * chosen for it, not derived from the theme.
 */
const BRAND_WORDMARK_WHITE = "/brand/gestionalo-logo-white.svg";

export interface CheckoutSummaryPanelProps {
  amountMinor: number | null;
  currency: string;
  /** The charge itemised, as the SERVER built it. Empty renders the total alone. */
  items: Array<{ title: string; amountMinor: number }>;
  /** Where "volver" goes — the RFQ this came from, or the requests list. */
  backHref: string;
  /**
   * Drops the brand-and-back row.
   *
   * For the EMBEDDED case: the mobile app opens this page inside a web view
   * that already draws its own navigation bar with its own back control, so
   * the panel's row is a second header and a second way out, one of which
   * leads somewhere the app cannot follow.
   */
  hideChrome?: boolean;
  /** Intercept the navigation (a page mid-upload wants to refuse it). */
  onBack?: () => void;
  backDisabled?: boolean;
}

/**
 * The brand half of a checkout: what is being charged, and the way back.
 *
 * ONE COMPONENT BECAUSE THERE ARE TWO CHECKOUT PAGES — card and bank transfer —
 * and they are the same payment seen from two sides. Writing this twice is how
 * the amount on one drifts from the amount on the other, which is the same
 * failure that put a professional's commission out of step with the charge
 * earlier today.
 *
 * Modelled on Stripe's hosted checkout: a full-bleed coloured half with the
 * amount as the page's hero, the form on plain ground opposite.
 *
 * bg-auth-surface, NOT bg-primary. Dark mode repurposes --color-primary from
 * brand navy to the mint BUTTON colour, which would paint this panel mint;
 * --color-auth-surface exists for exactly this and stays navy in both themes
 * (see index.css).
 */
export function CheckoutSummaryPanel({
  amountMinor,
  currency,
  items,
  backHref,
  onBack,
  backDisabled = false,
  hideChrome = false,
}: CheckoutSummaryPanelProps): JSX.Element {
  const { t } = useTranslation();

  const back = onBack ? (
    <button
      type="button"
      disabled={backDisabled}
      onClick={onBack}
      className="inline-flex items-center gap-2 rounded-md text-sm text-white/70 underline-offset-4 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 disabled:opacity-50"
    >
      <ArrowLeft className="size-4" />
      <img src={BRAND_WORDMARK_WHITE} alt="Gestionalo" className="h-6 w-auto" />
      <span className="sr-only">{t("pay.mercadoPagoBack")}</span>
    </button>
  ) : (
    <a
      href={backHref}
      className="inline-flex items-center gap-2 rounded-md text-sm text-white/70 underline-offset-4 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
    >
      <ArrowLeft className="size-4" />
      <img src={BRAND_WORDMARK_WHITE} alt="Gestionalo" className="h-6 w-auto" />
      <span className="sr-only">{t("pay.mercadoPagoBack")}</span>
    </a>
  );

  return (
    <div
      className={
        hideChrome
          ? "flex flex-col gap-5 bg-auth-surface px-5 py-5 text-white lg:min-h-svh lg:items-end"
          : "flex flex-col gap-8 bg-auth-surface px-6 py-8 text-white lg:min-h-svh lg:items-end lg:px-10 lg:py-14"
      }
    >
      {!hideChrome && <div className="w-full lg:max-w-sm">{back}</div>}

      {amountMinor == null ? (
        <div className="w-full lg:max-w-sm">
          <div className="h-10 w-40 animate-pulse rounded bg-white/10" />
        </div>
      ) : (
        <div className="w-full lg:max-w-sm">
          <p className="text-sm text-white/60">{t("pay.mercadoPagoPayFor")}</p>
          <p className="mt-1 text-4xl font-semibold tracking-tight tabular-nums">
            {formatUyMinor(amountMinor, currency)}
          </p>

          {items.length > 0 ? (
            <dl className="mt-8 space-y-3 border-t border-white/15 pt-6">
              {items.map((item, index) => (
                <div
                  // Derived per request from the checkout row, in a fixed
                  // order, with no ids of their own.
                  key={`${index}-${item.title}`}
                  className="flex items-baseline justify-between gap-6 text-sm"
                >
                  <dt className="min-w-0 text-white/80">
                    <span className="block truncate">{item.title}</span>
                  </dt>
                  <dd className="shrink-0 tabular-nums text-white">
                    {formatUyMinor(item.amountMinor, currency)}
                  </dd>
                </div>
              ))}
            </dl>
          ) : null}

          <div className="mt-6 flex items-baseline justify-between gap-6 border-t border-white/15 pt-6">
            <span className="whitespace-nowrap text-sm font-medium text-white">
              {t("pay.mercadoPagoTotalLabel")}
            </span>
            <span className="text-lg font-semibold tabular-nums text-white">
              {formatUyMinor(amountMinor, currency)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
